import React, { FC, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import { FeaturedTalentCard, Skeleton } from '@src/containers/Feed/FeaturedTalentCard';
import {
  useSessionQuery,
  useCreateVerifiedUserApplicantMutation,
  PaginatedVerifiedUserType,
  CompanyProfileStateEnum,
  PlaceTypeEnum,
  CustomerCalculatedSubscriptionStateEnum,
} from '@src/graphql/generated';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { getInviteMessage } from '@src/util/getInviteMessage';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { setModalContent, closeModal } from '@src/redux/actions/modal';
import { Image } from '@src/components/Image';
import { FinishCompanyProfileModal } from '@src/components/FinishCompanyProfileModal';
import { ConnectParams } from '@src/containers/Source/TalentCard/interfaces/connectParams.interface';
import { createLink } from '@src/util/createLink';
import { FEATURED_APPLICANTS_ROUTE } from '@src/Routes/links';
import { trackEvent } from '@src/metrics';
import { getCustomerStatus } from '@src/redux/selectors/recruiter';
import { SubscribeNotice } from '@src/components/SubscribeNotice';
import { Header } from './Header';
import { QueryParams } from './interfaces/queryParams.interface';
import { MOCK_CARDS } from './constants/mockCards';
import { useSearchVerifiedUsers } from './hooks';
import { Modal } from './Modal';
import { InfoModal } from './InfoModal';
import { FeedEmptyState } from './FeedEmptyState';
import { OnApplicantCardClickParams } from './interfaces/onApplicantCardClick.interface';
import { InfoSection } from './InfoSection';
import { CategoriesSection } from './CategoriesSection';
import './styles.scss';

export const FeaturedApplicants: FC = () => {
  const history = useHistory();

  const confirmCandidateParams = useRef<string | undefined>();

  const excludedUserIds = useRef<string[]>([]);

  const dispatch = useAppDispatch();

  const { queryParams, removeQueryParams } = useQueryParams<QueryParams>();

  const { data: sessionData } = useSessionQuery();

  const companyName = sessionData?.company.profile.name;

  const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);

  const recruiterCountry = sessionData?.currentRecruiter?.country || undefined;

  if (!queryParams.place && !queryParams.isRelocation && !queryParams.isRemote) {
    const path = createLink(FEATURED_APPLICANTS_ROUTE, {
      queryParams: { ...queryParams, place: { id: recruiterCountry?.id, type: PlaceTypeEnum.COUNTRY } },
    });
    history.replace(path);
  }

  const companyProfile = sessionData?.company.profile;

  const isPublished = companyProfile?.state === CompanyProfileStateEnum.PUBLISHED;

  const customerStatus = useAppSelector(getCustomerStatus);
  const companySubscription = customerStatus.planInfo.state;
  const hasActiveSubscription = companySubscription === CustomerCalculatedSubscriptionStateEnum.SUBSCRIPTION_ACTIVE;

  useEffect(() => {
    trackEvent({
      context: MetricContext.FEATURED_APPLICANTS_VIEW,
      interaction: MetricAction.VIEW_FEATURED_APPLICANTS_PAGE,
    });
  }, []);

  const recruiter = sessionData?.currentRecruiter;

  const [createVerifiedApplicant] = useCreateVerifiedUserApplicantMutation();

  const { feed, isLoading } =
    isPublished && hasActiveSubscription
      ? useSearchVerifiedUsers({
          excludedUserIds: excludedUserIds.current,
          queryParams: queryParams,
          recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
        })
      : { feed: [], isLoading: false };

  const applicants = !isPublished ? feed.slice(0, 3) : feed;

  const onConfirmTalent = async (userId: string, params?: ConnectParams): Promise<void> => {
    excludedUserIds.current.push(userId);

    const openingId = params?.openingId;
    const openingTitle = params?.openingTitle;
    const pitch = params?.pitch;

    if (userId) {
      confirmCandidateParams.current = undefined;

      await createVerifiedApplicant({
        update: cache => {
          cache.modify({
            fields: {
              recommendedUsers: (cachedRecommendedUsers: PaginatedVerifiedUserType) => {
                return {
                  ...cachedRecommendedUsers,
                  pagination: {
                    ...cachedRecommendedUsers.pagination,
                    totalCount: cachedRecommendedUsers.pagination.totalCount - 1,
                  },
                };
              },
            },
            id: cache.identify({
              __typename: 'OpeningType',
              id: openingId,
            }),
          });
        },
        variables: {
          message: pitch || getInviteMessage(companyName, openingTitle),
          openingId: openingId,
          userId: userId,
        },
      });

      setInvitedUserIds(prevUserIds => [...prevUserIds, userId]);

      trackEvent({
        context: MetricContext.FEATURED_APPLICANTS_VIEW,
        interaction: MetricAction.SEND_MESSAGE_TO_VERIFIED_TALENT,
        payload: {
          'Opening Id': openingId,
          'User Id': userId,
        },
      });
    }
  };

  const onApplicantCardClick = (
    userId: string,
    { isDirectConnectShownDefault }: OnApplicantCardClickParams = {},
  ): void => {
    trackEvent({
      context: MetricContext.FEATURED_APPLICANTS_VIEW,
      interaction: MetricAction.OPEN_FEATURED_APPLICANT_MODAL,
      payload: {
        'User Id': userId,
      },
    });

    dispatch(
      setModalContent({
        className: 'opening-modal-wrapper',
        config: {
          withoutPadding: true,
        },
        content: (
          <Modal
            excludedUserIds={excludedUserIds.current}
            invitedUserIds={invitedUserIds}
            isDirectConnectShownDefault={isDirectConnectShownDefault}
            onCloseModal={() => dispatch(closeModal())}
            onConfirmTalent={(userId, params) => onConfirmTalent(userId, params)}
            queryParams={queryParams}
            recruiterCurrencyCode={recruiter?.country?.currency || DEFAULT_CURRENCY_CODE}
            userId={userId}
          />
        ),
        onClose: () => {
          removeQueryParams(['userId']);
        },
      }),
    );
  };

  const onInfoIconClick = (): void => {
    dispatch(
      setModalContent({
        config: {
          withoutPadding: true,
        },
        content: <InfoModal onClose={() => dispatch(closeModal())} />,
      }),
    );
  };

  useEffect(() => {
    if (queryParams.userId && hasActiveSubscription) {
      onApplicantCardClick(queryParams.userId);
    }
  }, [queryParams.userId]);

  return (
    <div className="featured-applicants">
      <Header />

      <div className="featured-applicants__body-wrapper">
        <div className="featured-applicants__container">
          <div className="featured-applicants__main-section">
            <InfoSection onInfoIconClick={onInfoIconClick} />

            <hr className="featured-applicants__separator" />

            <div className="featured-applicants__headhunt-content">
              <div className="featured-applicants__headhunt-content-inner">
                <CategoriesSection />
              </div>
            </div>

            {!isLoading && !applicants.length && hasActiveSubscription && isPublished && (
              <div className="featured-applicants__empty-state-wrapper">
                <FeedEmptyState specialityId={queryParams.speciality} specialityName={queryParams.specialityName} />
              </div>
            )}

            <div
              className={classnames(
                'featured-applicants__featured-talent-cards',
                applicants.length < 3 && 'featured-applicants__featured-talent-cards--push-left',
              )}
            >
              {hasActiveSubscription &&
                isPublished &&
                applicants.map(userData => (
                  <FeaturedTalentCard
                    invitedUserIds={invitedUserIds}
                    isDirectConnect
                    isSaveActionVisible={false}
                    key={userData.user.id}
                    onCardClick={() => onApplicantCardClick(userData.user.id)}
                    onConfirmTalent={() => onApplicantCardClick(userData.user.id)}
                    userData={userData}
                    wrapperClassName="featured-applicants__card"
                  />
                ))}

              {(!isPublished || !hasActiveSubscription) &&
                MOCK_CARDS.map((card, index) => {
                  return (
                    <div className="featured-applicants__card featured-applicants__card--mock" key={index}>
                      <Image className="featured-applicants__card-image-mock" src={card} />
                    </div>
                  );
                })}

              {!hasActiveSubscription && <SubscribeNotice className="featured-applicants__subscribe-notice" />}

              {hasActiveSubscription && !isPublished && <FinishCompanyProfileModal />}

              {isLoading && (
                <>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
